"use client";

import { FlashList } from "@shopify/flash-list";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useAnimatedRef, useSharedValue } from "react-native-reanimated";
import { TOP_PHOTOS } from "src/api/featureFlags";
import useFooterGradientHeight from "src/components/FooterGradient/hooks/useFooterGradientHeight";
import {
	useGatheringContext,
	useGatheringStore,
	useParseFilterAndSortOptionsFromGatheringContext,
} from "src/contexts/GatheringContext";
import useGatheringPhotosWithLocal from "src/hooks/useGatheringPhotosWithLocal";
import useGlobalRefreshListener from "src/hooks/useGlobalRefreshListener";
import useIsFocused from "src/hooks/useIsFocused";
import useStateRef from "src/hooks/useStateRef";
import DragSelectionProvider from "src/providers/DragSelectionProvider";
import useHandleDragSelectionProviderScroll from "src/providers/DragSelectionProvider/hooks/useHandleDragSelectionProviderScroll";
import checkShouldRefresh from "src/screens/(internal)/(home)/Home/helpers/checkShouldRefresh";
import { List, useSafeArea } from "src/swsh-native";
import { VirtualList } from "src/swsh-native/List";
import GlobalRefreshTopic from "src/topics/GlobalRefreshTopic";
import useGatheringHomeComponentData from "../../hooks/useGatheringHomeComponentData";
import useGatheringTopPhotos from "../../hooks/useGatheringTopPhotos";
import { GatheringHomeItemType } from "../../types";
import GatheringHomeLoadingFooter from "../GatheringHomeLoadingFooter";
import useGatheringHomeNumColumns from "./hooks/useGatheringHomeNumColumns";
import useSetPreviewPhotos from "./hooks/useSetPreviewPhotos";

const keyExtractor = (item: GatheringHomeItemType) => item.itemKey;
const getItemType = (item: GatheringHomeItemType) => item.variant;

const GatheringHomeContent = () => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const numPhotos = useGatheringStore((state) => state.numPhotos);
	const { store } = useGatheringContext();
	const filterMode = useGatheringStore((state) => state.filterMode);

	const photoOptions = useParseFilterAndSortOptionsFromGatheringContext();
	const numColumns = useGatheringHomeNumColumns();

	const enabled = useIsFocused();
	const { photos, fetchMore, refresh, refreshing, loading } = useGatheringPhotosWithLocal(
		gatheringId,
		{
			...photoOptions,
			numColumns,
			filterMode,
			enabled,
			shouldInitialRefresh: checkShouldRefresh,
		},
	);

	useGlobalRefreshListener(refresh);
	useSetPreviewPhotos({ photos });
	const [topPhotosActive, setTopPhotosActive, topPhotosActiveRef] = useStateRef(
		photos.length > 0 && TOP_PHOTOS,
	);

	useEffect(() => {
		if (photos.length > 0 && TOP_PHOTOS && !topPhotosActiveRef.current) {
			setTopPhotosActive(true);
		}
	}, [photos.length, setTopPhotosActive, topPhotosActiveRef]);

	useEffect(() => {
		const { hasPhotos } = store.getState();
		if (hasPhotos) return;
		if (photos.length > 0 || (numPhotos ?? 0) > 0) {
			store.setState({ hasPhotos: true });
		} else if (!loading) {
			store.setState({ hasPhotos: false });
		}
	}, [loading, numPhotos, photos.length, store]);

	const {
		topPhotos,
		refresh: refreshTopPhotos,
		refreshing: topPhotosRefreshing,
		queryLoading,
	} = useGatheringTopPhotos({
		gatheringId,
		active: topPhotosActive,
	});
	useEffect(() => {
		if (topPhotos.length > 0) {
			store.setState({ hasTopPhotos: true });
		} else if (!queryLoading) {
			store.setState({ hasTopPhotos: false });
		}
	}, [queryLoading, store, topPhotos.length]);

	const [manualRefreshing, setManualRefreshing] = useState(false);
	useEffect(() => {
		const { loadingForNavBar, setLoadingForNavBar } = store.getState();
		const newLoadingForNavBar =
			((refreshing || topPhotosRefreshing) && !manualRefreshing) ||
			// Initial loading look better in nav bar
			(loading && photos.length === 0);
		if (newLoadingForNavBar !== loadingForNavBar) {
			setLoadingForNavBar(newLoadingForNavBar);
		}
	}, [manualRefreshing, refreshing, topPhotosRefreshing, photos.length, loading, store]);

	const scrollOffset = useSharedValue(0);
	const { handleScroll, isOverrideScrollingSV } =
		useHandleDragSelectionProviderScroll(scrollOffset);
	const animatedRef = useAnimatedRef<FlashList<GatheringHomeItemType>>();
	const [shouldScroll, setShouldScroll] = useState(true);
	const panRef = useRef(null);

	const { gatheringHomeItems, renderItem, isEmpty } = useGatheringHomeComponentData({
		numColumns,
		photos,
		topPhotos,
		loading,
		refreshing,
		scrollOffset,
	});

	const handleManualRefresh = useCallback(async () => {
		setManualRefreshing(true);
		const promises: Promise<any>[] = [];
		GlobalRefreshTopic.emit("refresh", {});
		const { viewMode } = store.getState();
		if (viewMode === "All" || viewMode === "Filter") {
			promises.push(refresh());
		}
		if (viewMode === "Top") {
			promises.push(refreshTopPhotos());
		}

		await Promise.all(promises).finally(() => {
			setManualRefreshing(false);
		});
	}, [refresh, refreshTopPhotos, store]);
	const handleFetchMore = useCallback(async () => {
		const { viewMode } = store.getState();
		if (
			viewMode === "All" ||
			viewMode === "Filter" ||
			viewMode === "Hidden" ||
			viewMode === "AllCaller"
		) {
			await fetchMore();
		}
	}, [fetchMore, store]);

	const safeArea = useSafeArea();

	const isSelecting = useGatheringStore((state) => state.selectMode === "Select");
	const footerHeight = useFooterGradientHeight();

	return (
		<DragSelectionProvider
			scrollOffset={scrollOffset}
			enabled={isSelecting}
			setShouldScroll={setShouldScroll}
			animatedRef={animatedRef}
			panRef={panRef}
			isOverrideScrollingSV={isOverrideScrollingSV}
		>
			<List
				animatedRef={animatedRef}
				overrideProps={{
					simultaneousHandler: panRef,
				}}
				drawDistance={800}
				onScroll={handleScroll}
				scrollEnabled={shouldScroll}
				WebListComponent={VirtualList}
				onEndReachedThreshold={2}
				refreshing={manualRefreshing}
				onRefresh={handleManualRefresh}
				data={gatheringHomeItems}
				keyExtractor={keyExtractor}
				getItemType={getItemType}
				renderItem={renderItem}
				estimatedItemSize={400}
				onEndReached={handleFetchMore}
				ListFooterComponent={<GatheringHomeLoadingFooter loading={loading} />}
				contentContainerStyle={{
					paddingBottom: safeArea.bottom + (isEmpty ? 0 : footerHeight),
				}}
			/>
		</DragSelectionProvider>
	);
};
export default memo(GatheringHomeContent);
